import { useState } from 'react';
import { Table, Form, Row, Col, Badge, Spinner, Alert, Pagination, Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageFade from '../../components/animation/PageFade';
import { StaggerFade } from '../../components/animation/StaggerFade';
import { useGetOrdersQuery, useUpdateOrderStatusMutation } from '../../services/api';
import type { ApiOrder } from '../../types';

const statusColor = (status: string) => {
  if (status === 'delivered') return 'success';
  if (status === 'shipped') return 'info';
  if (status === 'processing') return 'primary';
  if (status === 'cancelled') return 'danger';
  return 'warning';
};

const paymentStatusColor = (status: string) => {
  if (status === 'paid') return 'success';
  if (status === 'failed') return 'danger';
  return 'warning';
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const capitalizeFirst = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

interface OrderStatusDropdownProps {
  order: ApiOrder;
  onStatusChange: (orderId: string, newStatus: string) => void;
  isUpdating: boolean;
  updatingOrderId: string | null;
}

const OrderStatusDropdown = ({ order, onStatusChange, isUpdating, updatingOrderId }: OrderStatusDropdownProps) => {
  const statuses = ['pending', 'processing', 'shipped', 'delivered', 'cancelled'];
  const isThisOrderUpdating = isUpdating && updatingOrderId === order._id;

  return (
    <div className="d-flex align-items-center gap-2">
      <Form.Select
        size="sm"
        value={order.status}
        onChange={(e) => onStatusChange(order._id, e.target.value)}
        disabled={isUpdating}
        className={`bg-${statusColor(order.status)} text-white border-0`}
        style={{ minWidth: '120px', cursor: isUpdating ? 'wait' : 'pointer' }}
      >
        {statuses.map((status) => (
          <option key={status} value={status}>
            {capitalizeFirst(status)}
          </option>
        ))}
      </Form.Select>
      {isThisOrderUpdating && (
        <Spinner animation="border" size="sm" variant="primary" />
      )}
    </div>
  );
};

interface PaymentStatusDropdownProps {
  order: ApiOrder;
  onPaymentStatusChange: (orderId: string, newPaymentStatus: string) => void;
  isUpdating: boolean;
  updatingOrderId: string | null;
}

const PaymentStatusDropdown = ({ order, onPaymentStatusChange, isUpdating, updatingOrderId }: PaymentStatusDropdownProps) => {
  const paymentStatuses = ['pending', 'paid', 'failed'];
  const isThisOrderUpdating = isUpdating && updatingOrderId === order._id;

  return (
    <div className="d-flex align-items-center gap-2">
      <Form.Select
        size="sm"
        value={order.paymentStatus}
        onChange={(e) => onPaymentStatusChange(order._id, e.target.value)}
        disabled={isUpdating}
        className={`bg-${paymentStatusColor(order.paymentStatus)} text-white border-0`}
        style={{ minWidth: '100px', cursor: isUpdating ? 'wait' : 'pointer' }}
      >
        {paymentStatuses.map((status) => (
          <option key={status} value={status}>
            {capitalizeFirst(status)}
          </option>
        ))}
      </Form.Select>
      {isThisOrderUpdating && (
        <Spinner animation="border" size="sm" variant="primary" />
      )}
    </div>
  );
};

export default function Orders() {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [updatingOrderId, setUpdatingOrderId] = useState<string | null>(null);
  const [updatingPaymentOrderId, setUpdatingPaymentOrderId] = useState<string | null>(null);

  const { data, isLoading, isFetching, isError, error } = useGetOrdersQuery({
    page: currentPage,
    limit,
  });

  const [updateOrderStatus, { isLoading: isUpdating }] = useUpdateOrderStatusMutation();
  const [updatePaymentStatus, { isLoading: isUpdatingPayment }] = useUpdateOrderStatusMutation();

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    setUpdatingOrderId(orderId);
    try {
      const result = await updateOrderStatus({
        id: orderId,
        status: newStatus,
      }).unwrap();

      toast.success(result.message || 'Order status updated successfully!');
    } catch (err: any) {
      const errorMessage = err?.data?.message || 'Failed to update order status';
      toast.error(errorMessage);
      console.error('Error updating order status:', err);
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handlePaymentStatusChange = async (orderId: string, newPaymentStatus: string) => {
    setUpdatingPaymentOrderId(orderId);
    try {
      const result = await updatePaymentStatus({
        id: orderId,
        paymentStatus: newPaymentStatus,
      }).unwrap();

      toast.success(result.message || 'Payment status updated successfully!');
    } catch (err: any) {
      const errorMessage = err?.data?.message || 'Failed to update payment status';
      toast.error(errorMessage);
      console.error('Error updating payment status:', err);
    } finally {
      setUpdatingPaymentOrderId(null);
    }
  };

  const getCustomerName = (order: ApiOrder) => {
    if (order.userId) {
      return order.userId.name || order.userId.email;
    }
    return 'Guest';
  };

  if (isLoading) {
    return (
      <PageFade>
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading orders...</span>
          </Spinner>
          <p className="mt-3 text-muted">Loading orders...</p>
        </div>
      </PageFade>
    );
  }

  if (isError) {
    const errorMsg = (error as any)?.data?.message || 'Failed to load orders. Please check your connection and try again.';
    return (
      <PageFade>
        <Alert variant="danger">
          <Alert.Heading>Error Loading Orders</Alert.Heading>
          <p>{errorMsg}</p>
        </Alert>
      </PageFade>
    );
  }

  const orders = data?.data?.orders || [];
  const totalOrders = data?.data?.total || 0;
  const totalPages = data?.data?.pages || 1;

  return (
    <PageFade>
      <Row className="mb-3">
        <Col>
          <h3>Orders Management</h3>
          <p className="text-muted">
            Total Orders: EGP <strong>{totalOrders}</strong>
          </p>
        </Col>
      </Row>

      {orders.length === 0 ? (
        <Alert variant="info">
          <Alert.Heading>No Orders Found</Alert.Heading>
          <p>There are no orders in the system yet.</p>
        </Alert>
      ) : (
        <>
          <div className="position-relative">
            <Table hover responsive>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                <StaggerFade>
                  {orders.map((order) => (
                    <tr key={order._id}>
                      <td>
                        <Link to={`/orders/${order._id}`} state={{ order }}>
                          <small>{order._id.slice(-8)}</small>
                        </Link>
                      </td>
                      <td>
                        <div>
                          <strong>{getCustomerName(order)}</strong>
                        </div>
                        {order.userId && (
                          <small className="text-muted">{order.userId.email}</small>
                        )}
                        {!order.userId && (
                          <small className="text-muted">{order.shippingAddress.email}</small>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">{order.items.length} item(s)</small>
                      </td>
                      <td>
                        <strong>EGP {order.totalAmount.toFixed(2)}</strong>
                      </td>
                      <td>
                        <OrderStatusDropdown
                          order={order}
                          onStatusChange={handleStatusChange}
                          isUpdating={isUpdating}
                          updatingOrderId={updatingOrderId}
                        />
                      </td>
                      <td>
                        <PaymentStatusDropdown
                          order={order}
                          onPaymentStatusChange={handlePaymentStatusChange}
                          isUpdating={isUpdatingPayment}
                          updatingOrderId={updatingPaymentOrderId}
                        />
                      </td>
                      <td>
                        <small>{formatDate(order.createdAt)}</small>
                      </td>
                    </tr>
                  ))}
                </StaggerFade>
              </tbody>
            </Table>

            {/* Loading overlay during pagination */}
            {isFetching && !isLoading && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 10,
                }}
              >
                <Spinner animation="border" variant="primary">
                  <span className="visually-hidden">Loading page...</span>
                </Spinner>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <Row className="mt-4">
              <Col className="d-flex justify-content-center">
                <Pagination>
                  <Pagination.Prev
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  />

                  {[...Array(totalPages)].map((_, idx) => {
                    const page = idx + 1;
                    // Show first page, last page, current page, and pages around current
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <Pagination.Ellipsis key={page} disabled />;
                    }
                    return null;
                  })}

                  <Pagination.Next
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </Col>
            </Row>
          )}
        </>
      )}
    </PageFade>
  );
}
